import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { SelectField } from "../../components/forms/Field";
import { api, errMsg, formatDate } from "../../lib/api";
import { PrivateFile, StatusBadge, Td, Th } from "./shared";

const Detail = ({ label, value }) => (
  <div><p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p><p className="text-sm font-medium text-navy break-words">{value || "-"}</p></div>
);

const StatusEditor = ({ item, statuses, endpoint, onSaved }) => {
  const [status, setStatus] = useState(item.status);
  const [note, setNote] = useState(item.admin_note || "");
  const save = async () => {
    try {
      await api.patch(`${endpoint}/${item.id}`, { status, admin_note: note });
      toast.success("Updated"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 rounded-lg bg-slate-50 p-4">
      <SelectField value={status} onChange={setStatus} options={statuses} testId="admin-status-select" />
      <Textarea rows={2} placeholder="Internal note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-white" data-testid="admin-note-input" />
      <button onClick={save} className="btn-primary w-full" data-testid="admin-status-save">Save</button>
    </div>
  );
};

const AppDialog = ({ item, onClose, onSaved }) => (
  <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="application-detail-dialog">
      <DialogHeader><DialogTitle className="font-display text-navy">{item?.full_name} <span className="font-mono text-sm text-royal">{item?.ref_id}</span></DialogTitle></DialogHeader>
      {item && (
        <div className="grid gap-6 md:grid-cols-3">
          <div className="grid grid-cols-2 gap-4 md:col-span-2">
            <Detail label="Date of birth" value={item.dob} /><Detail label="Gender" value={item.gender} />
            <Detail label="Mobile" value={item.mobile} /><Detail label="WhatsApp" value={item.whatsapp} />
            <Detail label="Email" value={item.email} /><Detail label="District" value={item.district} />
            <div className="col-span-2"><Detail label="Address" value={item.address} /></div>
            <Detail label="Employer" value={item.employer} /><Detail label="Designation" value={item.designation} />
            <Detail label="Employment category" value={item.employment_category} /><Detail label="Experience" value={item.experience_years} />
            <Detail label="Membership category" value={item.membership_category} /><Detail label="Member no." value={item.member_no} />
            <PrivateFile fileId={item.photo_file_id} label="Photograph" testId="application-photo-view" />
            <PrivateFile fileId={item.id_proof_file_id} label="ID proof" testId="application-idproof-view" />
          </div>
          <StatusEditor item={item} statuses={["submitted", "under_review", "approved", "rejected"]} endpoint="/admin/applications" onSaved={onSaved} />
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const ApplicationsTab = () => {
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["admin-apps"], queryFn: () => api.get("/admin/applications").then((r) => r.data) });
  const saved = () => { qc.invalidateQueries({ queryKey: ["admin-apps"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); setSel(null); };
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full" data-testid="applications-table">
        <thead className="border-b bg-slate-50"><tr><Th>Ref</Th><Th>Name</Th><Th>Employer</Th><Th>District</Th><Th>Date</Th><Th>Status</Th><Th /></tr></thead>
        <tbody className="divide-y">
          {data.map((a, i) => (
            <tr key={a.id} data-testid={`application-row-${i}`}>
              <Td className="font-mono text-xs">{a.ref_id}</Td><Td className="font-semibold text-navy">{a.full_name}</Td><Td>{a.employer}</Td><Td>{a.district}</Td>
              <Td>{formatDate(a.created_at)}</Td><Td><StatusBadge status={a.status} /></Td>
              <Td><button onClick={() => setSel(a)} className="text-sm font-semibold text-royal" data-testid={`application-view-${i}`}>View</button></Td>
            </tr>
          ))}
        </tbody>
      </table>
      {!data.length && <p className="p-8 text-center text-sm text-slate-500">No applications yet.</p>}
      <AppDialog item={sel} onClose={() => setSel(null)} onSaved={saved} />
    </div>
  );
};

const CaseDialog = ({ item, endpoint, onClose, onSaved }) => (
  <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="case-detail-dialog">
      <DialogHeader><DialogTitle className="font-display text-navy">{item?.subject} <span className="font-mono text-sm text-royal">{item?.ref_id}</span></DialogTitle></DialogHeader>
      {item && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Detail label="Name" value={item.name} /><Detail label="Mobile" value={item.mobile} />
            <Detail label="Email" value={item.email} /><Detail label="Employer" value={item.employer} />
            <Detail label="Category" value={item.category} /><Detail label="Confidential" value={item.confidential ? "Yes" : "No"} />
            {item.urgency && <Detail label="Urgency" value={item.urgency} />}
          </div>
          <div className="whitespace-pre-wrap rounded-lg border border-slate-200 p-4 text-sm text-slate-700">{item.description}</div>
          <StatusEditor item={item} statuses={["received", "in_progress", "resolved", "closed"]} endpoint={endpoint} onSaved={onSaved} />
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const CasesTab = ({ endpoint, qkey }) => {
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const { data = [] } = useQuery({ queryKey: [qkey], queryFn: () => api.get(endpoint).then((r) => r.data) });
  const saved = () => { qc.invalidateQueries({ queryKey: [qkey] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); setSel(null); };
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full" data-testid={`${qkey}-table`}>
        <thead className="border-b bg-slate-50"><tr><Th>Ref</Th><Th>Name</Th><Th>Category</Th><Th>Subject</Th><Th>Date</Th><Th>Status</Th><Th /></tr></thead>
        <tbody className="divide-y">
          {data.map((c, i) => (
            <tr key={c.id} data-testid={`${qkey}-row-${i}`}>
              <Td className="font-mono text-xs">{c.ref_id}</Td><Td className="font-semibold text-navy">{c.name}</Td><Td>{c.category}</Td>
              <Td>{c.subject}{c.urgency === "urgent" && <span className="ml-2 rounded bg-red-100 px-1.5 text-[10px] font-bold text-red-700">URGENT</span>}</Td>
              <Td>{formatDate(c.created_at)}</Td><Td><StatusBadge status={c.status} /></Td>
              <Td><button onClick={() => setSel(c)} className="text-sm font-semibold text-royal" data-testid={`${qkey}-view-${i}`}>View</button></Td>
            </tr>
          ))}
        </tbody>
      </table>
      {!data.length && <p className="p-8 text-center text-sm text-slate-500">Nothing here yet.</p>}
      <CaseDialog item={sel} endpoint={endpoint} onClose={() => setSel(null)} onSaved={saved} />
    </div>
  );
};
